import "./App.css";
import "./index.css";
import { AppRoutes } from "../src/routes/";
import store from "./store/store";
import { Provider, useSelector } from "react-redux";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
// import NotificationPopup from "./components/Admin/component/Notification";
import { useEffect, useState } from "react";
import { requestForToken, getMessagingIfSupported } from "./firebase";
import { onMessage } from "firebase/messaging";
import { Capacitor } from "@capacitor/core";
import { PushNotifications } from "@capacitor/push-notifications";
import type {  PushNotification, Token } from "@capacitor/push-notifications";

const API_URL = import.meta.env.VITE_API_URL;

// Connect Device Popup (FCM)
const ConnectDevicePopup = () => {
  const [show, setShow] = useState(false);
  const [checking, setChecking] = useState(true);

  // ✅ Get user from Redux auth slice
  const user = useSelector((state: any) => state.auth.user); // adjust path if your slice is different

  useEffect(() => {
    if (!user) {
      // Don't show popup if user is logged out
      setShow(false);
      setChecking(false);
      return;
    }

    (async () => {
      const permission = Notification.permission;

      if (permission === "default") {
        setShow(true);
        setChecking(false);
        return;
      }

      if (permission === "denied") {
        toast.error(
          "You have blocked notifications. Please enable from browser settings."
        );
        setChecking(false);
        return;
      }

      const token = await requestForToken();
      if (!token) {
        setShow(true);
        setChecking(false);
        return;
      }

      try {
        const res = await fetch(
          `${API_URL}/api/token/check?token=${encodeURIComponent(token)}`
        );
        const data = await res.json();
        if (!data?.isRegistered) setShow(true);
      } catch (err) {
        console.error("Check token error:", err);
        setShow(true);
      }

      setChecking(false);
    })();
  }, [user]); // ✅ Re-run when login/logout changes

  const saveDeviceToken = async () => {
    try {
      const token = await requestForToken();
      if (!token) {
        toast.error("⚠️ Unable to get FCM token.");
        return;
      }

      const res = await fetch(`${API_URL}/api/token/save-token`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });

      if (!res.ok) throw new Error(await res.text());
      toast.success("✅ Device connected successfully!");
      setShow(false);
    } catch (err) {
      console.error("Token save error:", err);
      toast.error("Error connecting device.");
    }
  };

  const connectNow = async () => {
    try {
      if (Notification.permission === "granted") {
        return await saveDeviceToken();
      }

      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        toast.error("❌ Please allow notifications to connect your device.");
        return;
      }

      await saveDeviceToken();
    } catch (error) {
      console.error("❌ Connect device error:", error);
      toast.error("Error connecting device.");
    }
  };

  if (checking || !show) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0, 0, 0, 0.6)",
        backdropFilter: "blur(8px)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 9999,
        animation: "fadeIn 0.2s ease-out",
      }}
      onClick={() => setShow(false)}
    >
      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(20px) scale(0.95); } to { opacity: 1; transform: translateY(0) scale(1); } }
      `}</style>

      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 380,
          background: "linear-gradient(135deg, #e8ebe4ff 0%, rgba(17, 17, 17, 1) 100%)",
          borderRadius: 24,
          textAlign: "center",
          boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
          animation: "slideUp 0.3s ease-out",
          overflow: "hidden",
          position: "relative",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: -50,
            right: -50,
            width: 150,
            height: 150,
            background: "rgba(255,255,255,0.1)",
            borderRadius: "50%",
          }}
        />

        <div style={{ padding: 40 }}>
          <div
            style={{
              width: 64,
              height: 64,
              background: "rgba(255,255,255,0.2)",
              borderRadius: "50%",
              margin: "0 auto 20px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              backdropFilter: "blur(10px)",
            }}
          >
            <svg width="32" height="32" stroke="white" strokeWidth="2" fill="none">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
              <path d="M13.73 21a2 2 0 0 1-3.46 0" />
            </svg>
          </div>

          <h3 style={{ color: "#fff", fontSize: 24, marginBottom: 12 }}>
            Connect This Device
          </h3>

          <p style={{ color: "rgba(255,255,255,0.9)", marginBottom: 28 }}>
            Enable push notifications to receive important alerts & messages.
          </p>

          <button
            onClick={connectNow}
            style={{
              background: "#fff",
              color: "#667eea",
              padding: "14px 32px",
              width: "100%",
              borderRadius: 12,
              border: "none",
              fontSize: 15,
              fontWeight: 600,
              cursor: "pointer",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            }}
          >
            Connect Device
          </button>

          <button
            onClick={() => setShow(false)}
            style={{
              marginTop: 16,
              background: "transparent",
              border: "none",
              color: "rgba(255,255,255,0.8)",
              cursor: "pointer",
              fontSize: 14,
            }}
          >
            Maybe Later
          </button>
        </div>
      </div>
    </div>
  );
};

// App Content
const AppContent: React.FC = () => {
  const [showNotification, setShowNotification] = useState(false);

  // Web FCM
  useEffect(() => {
    if (Capacitor.getPlatform() !== "web") return; // ⛔ Stop on native

    if ("serviceWorker" in navigator) {
      navigator.serviceWorker
        .register("/firebase-messaging-sw.js")
        .then((reg) => console.log("✅ Firebase SW Registered:", reg))
        .catch(console.error);
    }
  }, []);

  // Web foreground message
  useEffect(() => {
    if (Capacitor.getPlatform() !== "web") return; // ⛔ Stop on native

    (async () => {
      const messaging = await getMessagingIfSupported();
      if (!messaging) return;

      onMessage(messaging, (payload) => {
        console.log("📩 Foreground message received:", payload);
        const title = payload.notification?.title || "Notification";
        const body = payload.notification?.body || "";
        toast.info(`${title} — ${body}`);

        if (typeof Notification !== "undefined" && Notification.permission === "granted") {
          navigator.serviceWorker?.getRegistration()?.then((reg) => {
            reg?.showNotification(title, {
              body,
              icon: "/firebase-logo.png",
              vibrate: [200, 100, 200],
              tag: Date.now().toString(),
              renotify: true,
            });
          });
        } else {
          Notification?.requestPermission?.();
        }

        setShowNotification(true);
      });
    })();
  }, []);

  // Mobile (Capacitor) FCM
  useEffect(() => {
    if (Capacitor.getPlatform() === "web") return; // ⛔ Skip on web

    PushNotifications.requestPermissions().then((result) => {
      if (result.receive === "granted") {
        PushNotifications.register();
      }
    });

    PushNotifications.addListener("registration", (token: Token) => {
      console.log("📲 Native FCM Token:", token.value);
      fetch(`${API_URL}/api/token/save-token`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: token.value }),
      })
        .then(() => toast.success("Native Device Connected"))
        .catch((err) => console.log("Token Save error:", err));
    });

    PushNotifications.addListener(
      "pushNotificationReceived",
      (notification: PushNotification) => {
        console.log("📩 Native Foreground:", notification);
        toast.info(`${notification.title} — ${notification.body}`);
        setShowNotification(true);
      }
    );
  }, []);

  return (
    <div className="relative">
      {/* {showNotification && (
        <NotificationPopup onClose={() => setShowNotification(false)} />
      )} */}
    </div>
  );
};


export default function App() {
  return (
    <Provider store={store}>
      <ConnectDevicePopup />
      <AppContent />
      <AppRoutes />
      <ToastContainer />
    </Provider>
  );
}